/**
 * Collaborative tldraw whiteboard (1-on-1 sync via FastAPI WebSocket).
 *
 * Strategy:
 *  - When the editor mounts, we open a WS to /api/ws/whiteboard/{lessonId}.
 *  - On every local store change, send {type:"update", changes} to peer.
 *  - On every incoming "update", apply via store.mergeRemoteChanges.
 *  - When peer joins (presence event), we re-emit our full snapshot so they
 *    can hydrate even if the server's snapshot cache is stale.
 *  - On mount we ask peer for snapshot (request_snapshot) in case we are
 *    second to join and the server cache hasn't seen one yet.
 *
 * Images and videos: tldraw natively supports drag-and-drop & paste of
 * images/videos which become assets stored in the local store; those assets
 * are synced as part of normal store updates.
 */
import React, { useCallback, useEffect, useRef } from "react";
import { Tldraw, getSnapshot, loadSnapshot } from "tldraw";
import "tldraw/tldraw.css";
import { WS_BASE } from "../api";

export default function Whiteboard({ lessonId, wsToken, role }) {
  const wsRef = useRef(null);
  const editorRef = useRef(null);
  const applyingRemoteRef = useRef(false);
  const unlistenRef = useRef(null);

  const onMount = useCallback(
    (editor) => {
      editorRef.current = editor;

      // Listen for local document changes; relay only user-originated updates.
      unlistenRef.current = editor.store.listen(
        (entry) => {
          if (applyingRemoteRef.current) return;
          const ws = wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          ws.send(
            JSON.stringify({ type: "update", changes: entry.changes })
          );
        },
        { source: "user", scope: "document" }
      );

      // Open the WebSocket once the editor is ready.
      const url = `${WS_BASE}/whiteboard/${lessonId}?token=${encodeURIComponent(wsToken)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        // Ask peer for an authoritative snapshot if they have one.
        ws.send(JSON.stringify({ type: "request_snapshot" }));
        // Also publish our current snapshot so the server can hydrate late joiners.
        try {
          const snap = getSnapshot(editor.store);
          ws.send(JSON.stringify({ type: "snapshot", data: snap }));
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("snapshot send failed", e);
        }
      };

      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }

        if (msg.type === "snapshot" && msg.data) {
          try {
            applyingRemoteRef.current = true;
            loadSnapshot(editor.store, msg.data);
          } finally {
            applyingRemoteRef.current = false;
          }
        } else if (msg.type === "update" && msg.changes) {
          try {
            applyingRemoteRef.current = true;
            editor.store.mergeRemoteChanges(() => {
              const { added, updated, removed } = msg.changes;
              if (added) editor.store.put(Object.values(added));
              if (updated) {
                editor.store.put(Object.values(updated).map(([, after]) => after));
              }
              if (removed) editor.store.remove(Object.keys(removed));
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("apply remote update failed", e);
          } finally {
            applyingRemoteRef.current = false;
          }
        } else if (msg.type === "request_snapshot") {
          try {
            const snap = getSnapshot(editor.store);
            ws.send(JSON.stringify({ type: "snapshot", data: snap }));
          } catch { /* no-op */ }
        }
      };

      ws.onerror = (e) => {
        // eslint-disable-next-line no-console
        console.warn("whiteboard ws error", e);
      };
    },
    [lessonId, wsToken]
  );

  useEffect(() => {
    return () => {
      try { unlistenRef.current?.(); } catch { /* no-op */ }
      try { wsRef.current?.close(); } catch { /* no-op */ }
      wsRef.current = null;
      editorRef.current = null;
    };
  }, []);

  return (
    <div
      data-testid="whiteboard-container"
      data-role={role}
      className="w-full h-full"
    >
      <Tldraw onMount={onMount} />
    </div>
  );
}
